import type { RouteLocationNormalizedLoaded, Router } from "vue-router";
import { toast } from "vue-sonner";
import { searchIntermediateOrderResultByIdentifier } from "../../services/intermediateOrderService.ts";
import type { useAppStore } from "../../stores/app.ts";

const INTERMEDIATE_ORDER_ROUTE_NAME =
  "dashboard-intermediate-order-sku-distribution";

const BULK_ORDER_PREFIX = "z";
const INTERMEDIATE_ORDER_PREFIX = "i";

type BulkIntermediateOrderType = "bulk-order" | "intermediate-order";

export function classifyBulkOrIntermediateOrderIdentifier(
  rawInput: string
): BulkIntermediateOrderType | undefined {
  const normalized = rawInput.trim().toLowerCase();
  if (normalized.startsWith(BULK_ORDER_PREFIX)) return "bulk-order";
  if (normalized.startsWith(INTERMEDIATE_ORDER_PREFIX)) {
    return "intermediate-order";
  }
  return undefined;
}

/**
 * Detects whether the given header/search input targets the Bulk Order
 * ("Z..." identifiers) or Intermediate PO ("IPO..." identifiers) search flow.
 * Both prefixes route to the same bulk/intermediate order search + page.
 */
export function matchesBulkOrIntermediateOrderPrefix(
  rawInput: string
): boolean {
  return classifyBulkOrIntermediateOrderIdentifier(rawInput) !== undefined;
}

type AppStore = ReturnType<typeof useAppStore>;

function toErrorMessage(error: unknown): string {
  return error instanceof Error
    ? error.message
    : "An unexpected error occurred.";
}

/**
 * Runs the Bulk Order / Intermediate PO identifier search against the given
 * app store, without navigating. Shared by the initial search
 * (searchBulkIntermediateOrdersAndNavigate) and the standalone refresh action
 * (refreshIntermediateOrderSearch) so both go through the same
 * success/error/loading handling.
 */
async function runIntermediateOrderSearch(
  query: string,
  appStore: AppStore
): Promise<void> {
  appStore.setIntermediateOrderLastQuery(query);
  appStore.searching = true;
  appStore.setIntermediateOrderSearching(true);
  try {
    const result = await searchIntermediateOrderResultByIdentifier(query);
    appStore.setIntermediateOrderSearchResults(
      result.rows,
      result.bulkAllocations,
      result.diagnostics
    );
    appStore.setIntermediateOrderSearchError(undefined);
  } catch (error) {
    const message = toErrorMessage(error);
    appStore.setIntermediateOrderSearchError(message);
    toast.error(`Bulk/Intermediate Order search failed: ${message}`);
  } finally {
    appStore.searching = false;
    appStore.setIntermediateOrderSearching(false);
  }
}

/**
 * Runs the Bulk Order / Intermediate PO identifier search and navigates to the
 * intermediate-order SKU distribution page if not already there. Shared by the
 * header search input (AppHeader.vue) and the "Bulk Order Lookup" search panel
 * tab (OpenPurchaseOrdersSearchPanel.vue) so both entry points behave
 * identically.
 */
export async function searchBulkIntermediateOrdersAndNavigate(args: {
  query: string;
  appStore: AppStore;
  router: Router;
  route: RouteLocationNormalizedLoaded;
}): Promise<void> {
  const { query, appStore, router, route } = args;
  const trimmedQuery = query.trim();
  if (!trimmedQuery) {
    return;
  }

  await runIntermediateOrderSearch(trimmedQuery, appStore);

  // Navigate after the search settles (success or failure) so the current
  // page's grid, if different from the IO page, unmounts while still in its
  // loading state rather than reacting to a state change right as it's
  // destroyed. Errors are surfaced via toast + intermediateOrderSearchError,
  // not by blocking navigation.
  if (route.name !== INTERMEDIATE_ORDER_ROUTE_NAME) {
    await router.push({ name: INTERMEDIATE_ORDER_ROUTE_NAME });
  }
}

/**
 * Re-runs the last Bulk Order / Intermediate PO search, if any, without
 * navigating. Used by the "Refresh" action on the Intermediate Order SKU
 * Distribution page so it can show the most current results on demand.
 */
export async function refreshIntermediateOrderSearch(
  appStore: AppStore
): Promise<void> {
  const query = appStore.intermediateOrderLastQuery?.trim();
  if (!query) {
    return;
  }

  await runIntermediateOrderSearch(query, appStore);
}
